/** Docs (UA): see modules/game/DOCS.md — «Спіймай бал» mini-game */

import { MACHINE_TYPES } from '../../shared/constants';
import type { VendingMachineTypeId } from '../../shared/types';

export type GameTheme = 'coffee' | 'popcorn' | 'snack' | 'water';
export type ItemKind = 'seed' | 'gold' | 'bug' | 'mult';

/**
 * Gameplay balance. Values are NOT arbitrary: structure calibrated by
 * Monte-Carlo simulation (Personal wiki: Raw/Files/catch-game-sim.mjs).
 * THRESHOLD is the single operational tuning knob. Business decision
 * 24.08: 1000 = a deliberately generous ~84% simulated winners (the
 * simulation-neutral 50-60% window sat at 1200). Change it alone;
 * the rest is structure.
 */
export const GAME_CONFIG = {
    DURATION: 60,
    LIVES: 3,
    THRESHOLD: 1000,
    SCORE: { seed: 10, gold: 30 },
    /** Gold falls 25% faster than a regular item (fall time × 0.8). */
    GOLD_FALL_FACTOR: 0.8,
    MULTIPLIER: { value: 2, duration: 8, spawnAt: [34, 47] },
    /** Multiplier lollipop falls 20% slower — it is a gift, not a challenge. */
    MULT_FALL_FACTOR: 1.2,
    IFRAMES: 1.0,
    /** width = ширина ТІЛА ловця (56px на 390) — усі теми зведені до неї,
     *  інакше склянка (44px) і лоток (76px) робили б теми різними за складністю.
     *  56, а не 68: при висоті тіла ~48 ширші ловці читались як плоскі тази. */
    CUP: { width: 0.1436, maxSpeed: 1.7, smoothing: 14 },
    /**
     * Радіус ловитви на тип, нормалізований до ширини поля (390). Дорівнює
     * ПОЛОВИНІ видимого тіла спрайта: спільні 0.03 (11.7px) були менші за
     * будь-який предмет. Золото має тіло того ж розміру, що й зерно, тож і
     * радіус той самий — воно виграє кольором, німбом і обертанням.
     */
    ITEM_RADIUS: {
        seed: 0.046,
        gold: 0.046,
        bug: 0.033,
        mult: 0.054,
    } as Record<ItemKind, number>,
    SPAWN_MARGIN: 0.08,
    /**
     * Difficulty curve control points, lerped between. All spawn flows are
     * quasi-regular schedules with jitter (seeds ±40%, gold/bug ±20%), NOT
     * probabilities: every session gets the same budget of points and
     * threats — a loss can never be blamed on a stingy roll.
     */
    PHASES: [
        { t: 0, seedRate: 0.9, fallTime: 2.3, goldEvery: 6.0, badEvery: 8.0 },
        { t: 6, seedRate: 1.0, fallTime: 2.1, goldEvery: 6.0, badEvery: 8.0 },
        { t: 20, seedRate: 1.1, fallTime: 1.8, goldEvery: 5.0, badEvery: 6.0 },
        { t: 40, seedRate: 1.2, fallTime: 1.5, goldEvery: 4.0, badEvery: 5.0 },
        { t: 50, seedRate: 1.3, fallTime: 1.4, goldEvery: 2.2, badEvery: 5.0 },
        { t: 60, seedRate: 1.3, fallTime: 1.35, goldEvery: 2.0, badEvery: 5.0 },
    ],
    FIRST_GOLD_AT: 7,
    FIRST_BAD_AT: 8.5,
    SEED_JITTER: 0.4,
    SPAWN_JITTER: 0.2,
    /** Golden rain is pure joy: no bugs spawn in the final seconds. */
    NO_BUGS_AFTER: 50,
} as const;

/**
 * Espresso Brand (v4) palette — Figma page «Game v4 — Espresso Brand».
 * The world stays coffee (product themes read on it); ProstoPay enters as
 * roles, not as the backdrop: indigo canopy/floor, warm ember, logo gold,
 * CTA blue/violet accents, product error red. Feedback 24.08.
 */
export const GAME_PALETTE = {
    skyTop: '#22120E',
    skyBottom: '#5A2E1C',
    slab: '#101A3A',
    slabDeep: '#0A1027',
    track: '#1E2A52',
    ember: '#F77F00',
    bokeh1: '#7A4426',
    bokeh2: '#9C5A31',
    ink: '#1A0F0B',
    cream: '#FFF6E7',
    uiText: '#2A1A14',
    accent: '#5C81ED',
    accentAlt: '#B984FA',
    gold: '#FFD800',
    goldGlow: '#FFF3B0',
    goldStroke: '#A26A00',
    danger: '#FF2E4D',
    success: '#81C784',
    successDeep: '#2E7D32',
    bugBody: '#241019',
} as const;

/** Canvas needs rgba() strings; the palette above stays the single source. */
export const rgbaFromHex = (hex: string, alpha: number): string => {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

export interface GameThemeColors {
    light: string;
    deep: string;
}

/** Item body gradients per theme (light stop dominant — see DOCS.md). */
export const THEME_ITEM_COLORS: Record<GameTheme, GameThemeColors> = {
    coffee: { light: '#C98A4B', deep: '#8A5220' },
    popcorn: { light: '#FFF6E7', deep: '#F2C14E' },
    snack: { light: '#B984FA', deep: '#8E5BD0' },
    water: { light: '#9FEEF9', deep: '#3EC4DB' },
};

/**
 * Theme = product of the machine, exhaustive over every type (same shape as
 * LOYALTY_ICON_BY_MACHINE_TYPE in loyalty.constants.ts). Coffee is the
 * fallback: ~92% of the fleet is coffee, and the design spec §3.7 asks for it.
 */
export const THEME_BY_MACHINE_TYPE: Record<VendingMachineTypeId, GameTheme> = {
    [MACHINE_TYPES.SNACK_MACHINE]: 'snack',
    [MACHINE_TYPES.COFFEE_AUTOMAT]: 'coffee',
    [MACHINE_TYPES.SNACK_MACHINE_SPARKA]: 'snack',
    [MACHINE_TYPES.COFFEE_MACHINE_SPARKA]: 'coffee',
    [MACHINE_TYPES.COFFEE_MACHINE]: 'coffee',
    [MACHINE_TYPES.FRESH_MACHINE]: 'coffee',
    [MACHINE_TYPES.ICE_CREAM_MACHINE]: 'snack',
    [MACHINE_TYPES.CIGARETTE_MACHINE]: 'coffee',
    [MACHINE_TYPES.CONDOM_MACHINE]: 'coffee',
    [MACHINE_TYPES.FRIDGE]: 'snack',
    [MACHINE_TYPES.WATER_MACHINE_PULS]: 'water',
    [MACHINE_TYPES.SNACK_MACHINE_PULS]: 'snack',
    [MACHINE_TYPES.FRIDGE_SMART]: 'snack',
    [MACHINE_TYPES.PULSE_MACHINE]: 'coffee',
    [MACHINE_TYPES.CAR_WASH_MACHINE]: 'water',
    [MACHINE_TYPES.POPCORN_PULSE_MACHINE]: 'popcorn',
};

/** Theme changes skins only — physics, budgets and threshold live above. */
export const themeForMachineType = (
    typeId: VendingMachineTypeId | undefined,
): GameTheme => (typeId && THEME_BY_MACHINE_TYPE[typeId]) || 'coffee';

export const GAME_RECORD_STORAGE_KEY = 'game_catch_record';
export const GAME_SOUND_STORAGE_KEY = 'game_catch_sound';
export const gameTryStorageKey = (orderId: number | string): string =>
    `game_catch_try_${orderId}`;
